import { formatTimeRangeWithPreferences } from "@workspace/shared-utils";
import type { Review } from "@workspace/types";
import type { DateTimePreferences } from "@workspace/types/src/entities/user";
import {
	getExportColors,
	validateExportColors,
} from "./export-colors";

/**
 * Loads an image with timeout
 * @returns Promise that resolves with the loaded image
 */
async function loadImage(url: string, timeout = 3000): Promise<HTMLImageElement> {
	return new Promise((resolve, reject) => {
		const img = new Image();
		img.crossOrigin = "anonymous";
		const timer = setTimeout(() => reject(new Error("Image load timeout")), timeout);
		img.onload = () => { clearTimeout(timer); resolve(img); };
		img.onerror = () => { clearTimeout(timer); reject(new Error("Image load failed")); };
		img.src = url;
	});
}

/**
 * Loads Inter fonts for canvas rendering with fallback to system fonts
 * Waits for fonts to be ready before returning
 * @returns Font family string to use in canvas ctx.font
 */
async function loadInterFont(): Promise<string> {
	// Check if browser supports Font Loading API
	if (!("fonts" in document)) {
		console.warn(
			"⚠️ Font Loading API not supported, using system fonts for export",
		);
		return "Arial, Helvetica, sans-serif";
	}

	try {
		// Wait for all fonts to load (including Inter from CSS)
		await document.fonts.ready;

		// Check if Inter font is available
		const interAvailable = document.fonts.check("12px Inter");

		if (interAvailable) {
			console.log("✓ Inter font loaded successfully for schedule export");
			return "Inter, Arial, Helvetica, sans-serif";
		} else {
			console.warn(
				"⚠️ Inter font not available, using system fonts for export",
			);
			return "Arial, Helvetica, sans-serif";
		}
	} catch (error) {
		console.error(
			"⚠️ Error loading fonts for export, falling back to system fonts:",
			error,
		);
		return "Arial, Helvetica, sans-serif";
	}
}

interface PhoneSize {
	width: number;
	height: number;
	label: string;
}

interface ExportOptions {
	festivalName: string;
	festivalSlug: string;
	selectedDay: string;
	username?: string;
	userProfileImageUrl?: string;
	theme: "light" | "dark";
	sortOrder: "standard" | "inverse";
	phoneSize: PhoneSize;
	calendarData: Array<{
		id: string;
		artist: string;
		stage: string;
		time: string;
		startHour: number;
		duration: number;
		imageUrl?: string | null;
		status: {
			isInSchedule: boolean;
			sawLive: boolean;
			sawStream: boolean;
			mustSee: boolean;
		};
	}>;
	reviews: Review[];
	selectedStages?: string[];
	userPreferences?: DateTimePreferences;
	eventType?: "main_festival" | "pre_party" | "after_party";
	availableDays?: string[];
	showArtistPhotos?: boolean;
	gradientColors?: string[];
}

// Static Mesh Gradient shader (from https://shaders.paper.design/static-mesh-gradient)
// Tuned for smooth Apple-style wallpaper: high mixing, minimal wave, zero grain
const SHADER_VERT = `#version 300 es
in vec2 a_position;
out vec2 v_objectUV;
void main() {
  gl_Position = vec4(a_position, 0.0, 1.0);
  v_objectUV = a_position * 0.5;
}`;

const SHADER_FRAG = `#version 300 es
precision mediump float;

#define TWO_PI 6.28318530718

float hash21(vec2 p) {
  p = fract(p * vec2(0.3183099, 0.3678794)) + 0.1;
  p += dot(p, p + 19.19);
  return fract(p.x * p.y);
}

vec2 rotate(vec2 uv, float th) {
  return mat2(cos(th), sin(th), -sin(th), cos(th)) * uv;
}

uniform vec4 u_colors[10];
uniform float u_colorsCount;
uniform float u_positions;
uniform float u_waveX;
uniform float u_waveXShift;
uniform float u_waveY;
uniform float u_waveYShift;
uniform float u_mixing;
uniform float u_grainMixer;
uniform float u_grainOverlay;

in vec2 v_objectUV;
out vec4 fragColor;

float valueNoise(vec2 st) {
  vec2 i = floor(st);
  vec2 f = fract(st);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  vec2 u = f * f * (3.0 - 2.0 * f);
  float x1 = mix(a, b, u.x);
  float x2 = mix(c, d, u.x);
  return mix(x1, x2, u.y);
}

float noise(vec2 n, vec2 seedOffset) {
  return valueNoise(n + seedOffset);
}

vec2 getPosition(int i, float t) {
  float a = float(i) * .37;
  float b = .6 + mod(float(i), 3.) * .3;
  float c = .8 + mod(float(i + 1), 4.) * 0.25;
  float x = sin(t * b + a);
  float y = cos(t * c + a * 1.5);
  return .5 + .5 * vec2(x, y);
}

void main() {
  vec2 uv = v_objectUV;
  uv += .5;
  vec2 grainUV = uv * 1000.;
  float grain = noise(grainUV, vec2(0.));
  float mixerGrain = .4 * u_grainMixer * (grain - .5);
  float radius = smoothstep(0., 1., length(uv - .5));
  float center = 1. - radius;
  for (float i = 1.; i <= 2.; i++) {
    uv.x += u_waveX * center / i * cos(TWO_PI * u_waveXShift + i * 2. * smoothstep(.0, 1., uv.y));
    uv.y += u_waveY * center / i * cos(TWO_PI * u_waveYShift + i * 2. * smoothstep(.0, 1., uv.x));
  }
  vec3 color = vec3(0.);
  float opacity = 0.;
  float totalWeight = 0.;
  float positionSeed = 25. + .33 * u_positions;
  for (int i = 0; i < 10; i++) {
    if (i >= int(u_colorsCount)) break;
    vec2 pos = getPosition(i, positionSeed) + mixerGrain;
    float dist = length(uv - pos);
    vec3 colorFraction = u_colors[i].rgb * u_colors[i].a;
    float opacityFraction = u_colors[i].a;
    float mixing = pow(u_mixing, .7);
    float power = mix(2., 1., mixing);
    dist = pow(dist, power);
    float w = 1. / (dist + 1e-3);
    float baseSharpness = mix(.0, 8., clamp(w, 0., 1.));
    float sharpness = mix(baseSharpness, 1., mixing);
    w = pow(w, sharpness);
    color += colorFraction * w;
    opacity += opacityFraction * w;
    totalWeight += w;
  }
  color /= max(1e-4, totalWeight);
  opacity /= max(1e-4, totalWeight);
  float grainOverlay = valueNoise(rotate(grainUV, 1.) + vec2(3.));
  grainOverlay = mix(grainOverlay, valueNoise(rotate(grainUV, 2.) + vec2(-1.)), .5);
  grainOverlay = pow(grainOverlay, 1.3);
  float grainOverlayV = grainOverlay * 2. - 1.;
  vec3 grainOverlayColor = vec3(step(0., grainOverlayV));
  float grainOverlayStrength = u_grainOverlay * abs(grainOverlayV);
  grainOverlayStrength = pow(grainOverlayStrength, .8);
  color = mix(color, grainOverlayColor, .35 * grainOverlayStrength);
  opacity += .5 * grainOverlayStrength;
  opacity = clamp(opacity, 0., 1.);
  fragColor = vec4(color, opacity);
}`;

// Default gradient colors (Radix violet/plum/indigo dark step 4-5)
const DEFAULT_GRADIENT_COLORS = ["#2f2567", "#3a2359", "#1f2c5c"];

/** Convert hex color string to GL float array [r, g, b, a] */
function hexToGlColor(hex: string): [number, number, number, number] {
	const h = hex.replace("#", "");
	return [
		Number.parseInt(h.slice(0, 2), 16) / 255,
		Number.parseInt(h.slice(2, 4), 16) / 255,
		Number.parseInt(h.slice(4, 6), 16) / 255,
		1.0,
	];
}

/**
 * Renders mesh gradient background using WebGL shader.
 * Tuned for smooth, solid Apple-style look: high mixing (0.99), low wave, zero grain.
 * Falls back silently if WebGL2 is not available.
 */
function renderGradientBackground(
	ctx: CanvasRenderingContext2D,
	width: number,
	height: number,
	colors?: string[],
): void {
	const gradientHex = colors?.length ? colors : DEFAULT_GRADIENT_COLORS;
	const glColors = gradientHex.map(hexToGlColor);
	const glCanvas = document.createElement("canvas");
	glCanvas.width = width;
	glCanvas.height = height;
	const gl = glCanvas.getContext("webgl2");
	if (!gl) return;

	const maxDims = gl.getParameter(gl.MAX_VIEWPORT_DIMS) as [number, number];
	if (width > maxDims[0] || height > maxDims[1]) return;

	try {
		const compileShader = (type: number, src: string): WebGLShader => {
			const shader = gl.createShader(type)!;
			gl.shaderSource(shader, src);
			gl.compileShader(shader);
			if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
				const log = gl.getShaderInfoLog(shader);
				gl.deleteShader(shader);
				throw new Error(log || "Shader compile failed");
			}
			return shader;
		};

		const vert = compileShader(gl.VERTEX_SHADER, SHADER_VERT);
		const frag = compileShader(gl.FRAGMENT_SHADER, SHADER_FRAG);

		const program = gl.createProgram()!;
		gl.attachShader(program, vert);
		gl.attachShader(program, frag);
		gl.linkProgram(program);
		if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
			throw new Error(gl.getProgramInfoLog(program) || "Link failed");
		}
		gl.useProgram(program);

		const buf = gl.createBuffer();
		gl.bindBuffer(gl.ARRAY_BUFFER, buf);
		gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
		const posLoc = gl.getAttribLocation(program, "a_position");
		gl.enableVertexAttribArray(posLoc);
		gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

		const colorsFlat = new Float32Array(40);
		for (let i = 0; i < glColors.length; i++) {
			colorsFlat[i * 4] = glColors[i][0];
			colorsFlat[i * 4 + 1] = glColors[i][1];
			colorsFlat[i * 4 + 2] = glColors[i][2];
			colorsFlat[i * 4 + 3] = glColors[i][3];
		}
		gl.uniform4fv(gl.getUniformLocation(program, "u_colors"), colorsFlat);
		gl.uniform1f(gl.getUniformLocation(program, "u_colorsCount"), glColors.length);

		// Smooth Apple-style params: very high mixing, gentle wave, zero grain
		gl.uniform1f(gl.getUniformLocation(program, "u_positions"), 2.0);
		gl.uniform1f(gl.getUniformLocation(program, "u_waveX"), 0.3);
		gl.uniform1f(gl.getUniformLocation(program, "u_waveXShift"), 0.6);
		gl.uniform1f(gl.getUniformLocation(program, "u_waveY"), 0.3);
		gl.uniform1f(gl.getUniformLocation(program, "u_waveYShift"), 0.21);
		gl.uniform1f(gl.getUniformLocation(program, "u_mixing"), 0.99);
		gl.uniform1f(gl.getUniformLocation(program, "u_grainMixer"), 0.0);
		gl.uniform1f(gl.getUniformLocation(program, "u_grainOverlay"), 0.0);

		gl.viewport(0, 0, width, height);
		gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
		ctx.drawImage(glCanvas, 0, 0);

		gl.deleteShader(vert);
		gl.deleteShader(frag);
		gl.deleteProgram(program);
		gl.deleteBuffer(buf);
	} catch {
		// Silently fall back to solid color background
	}
}

const PADDING = 34; // Minimal side margins (20 * 1.7)
const HEADER_HEIGHT = 2000; // Move calendar up a bit
const FOOTER_HEIGHT = 400; // More space at bottom
const SET_ITEM_HEIGHT = 80;
const SET_ITEM_SPACING = 12;

// Helper function to convert time strings like "4:00PM" to "16:00" format
function convertTo24Hour(timeStr: string): string {
	const cleanTime = timeStr.trim().toUpperCase();

	// Check if it's already in 24-hour format (no AM/PM)
	if (!/[AP]M/.test(cleanTime)) {
		return cleanTime.padStart(5, "0"); // Ensure HH:MM format
	}

	const timeMatch = cleanTime.match(/^(\d{1,2}):(\d{2})(AM|PM)$/);
	if (!timeMatch) {
		throw new Error(`Invalid time format: ${timeStr}`);
	}

	const [, hourStr, minute, ampm] = timeMatch;

	if (!hourStr || !minute || !ampm) {
		throw new Error(`Invalid time format: ${timeStr}`);
	}

	let hour = Number.parseInt(hourStr, 10);

	if (ampm === "AM") {
		if (hour === 12) hour = 0; // 12:00AM becomes 00:00
	} else {
		if (hour !== 12) hour += 12; // PM hours except 12:00PM
	}

	return `${hour.toString().padStart(2, "0")}:${minute}`;
}

interface ColorScheme {
	background: string;
	text: string;
	border: string;
	card: string;
	textMuted: string;
	placeholder: string;
	placeholderIcon: string;
	primary: string;
	mustSee: string;
	wantToSee: string;
	sawLive: string;
	sawStream: string;
}

// Connector patterns to render muted (B2B, pres., ×, x, +, &, vs, w/, & Friends, etc.)
// Handles: mid-text (" B2B "), start-of-line ("pres. "), and end-of-line (" pres." or " B2B...")
// Full list from getB2BDisplayParts: B2B, &, ×, +, vs., F2F, B3B, with, pres., & Friends, w/
const CONNECTOR_PATTERNS_MID = /(\s+(?:B2B|b2b|pres\.|×|vs\.?|F2F|B3B|with|w\/|& Friends)\s+|\s+[x+&]\s+)/g;
const CONNECTOR_PATTERNS_START = /^((?:B2B|b2b|pres\.|×|vs\.?|F2F|B3B|with|w\/|& Friends)\s+|[x+&]\s+)/;
const CONNECTOR_PATTERNS_END = /(\s+(?:B2B|b2b|pres\.?|×|vs\.?|F2F|B3B|with|w\/|& Friends?)\.{0,3}$|\s+[x+&]\.{0,3}$)/;

/**
 * Parses artist name into segments with type info for styling
 * e.g., "Excision B2B Subtronics" → [{text: "Excision", isConnector: false}, {text: " B2B ", isConnector: true}, {text: "Subtronics", isConnector: false}]
 * Also handles:
 * - Wrapped lines like "pres. HOLO" → [{text: "pres. ", isConnector: true}, {text: "HOLO", isConnector: false}]
 * - Truncated lines like "Gareth Emery pres..." → [{text: "Gareth Emery", isConnector: false}, {text: " pres...", isConnector: true}]
 */
function parseArtistSegments(artistName: string): Array<{ text: string; isConnector: boolean }> {
	const segments: Array<{ text: string; isConnector: boolean }> = [];
	let remaining = artistName;

	// First, check for connector at the start of the line (for wrapped text like "pres. HOLO")
	const startMatch = remaining.match(CONNECTOR_PATTERNS_START);
	if (startMatch) {
		segments.push({ text: startMatch[0], isConnector: true });
		remaining = remaining.slice(startMatch[0].length);
	}

	// Check for connector at the end of the line (for truncated text like "Gareth Emery pres...")
	const endMatch = remaining.match(CONNECTOR_PATTERNS_END);
	let endConnector: string | null = null;
	if (endMatch) {
		endConnector = endMatch[0];
		remaining = remaining.slice(0, -endConnector.length);
	}

	// Now process mid-text connectors
	let lastIndex = 0;
	let match: RegExpExecArray | null;

	// Reset regex state
	CONNECTOR_PATTERNS_MID.lastIndex = 0;

	while ((match = CONNECTOR_PATTERNS_MID.exec(remaining)) !== null) {
		// Add text before the connector
		if (match.index > lastIndex) {
			segments.push({ text: remaining.slice(lastIndex, match.index), isConnector: false });
		}
		// Add the connector
		segments.push({ text: match[0], isConnector: true });
		lastIndex = match.index + match[0].length;
	}

	// Add remaining text (after mid-text connectors, before end connector)
	if (lastIndex < remaining.length) {
		segments.push({ text: remaining.slice(lastIndex), isConnector: false });
	}

	// Add end connector if found
	if (endConnector) {
		segments.push({ text: endConnector, isConnector: true });
	}

	return segments.length > 0 ? segments : [{ text: artistName, isConnector: false }];
}

// Helper function for grid export
async function exportGridSchedule(
	ctx: CanvasRenderingContext2D,
	canvas: HTMLCanvasElement,
	options: ExportOptions & { colors: ColorScheme },
	roundRect: (
		ctx: CanvasRenderingContext2D,
		x: number,
		y: number,
		width: number,
		height: number,
		radius: number,
	) => void,
	fontFamily: string,
): Promise<void> {
	const {
		calendarData,
		selectedStages = [],
		colors,
		selectedDay,
		festivalName,
		sortOrder,
		eventType = "main_festival",
		showArtistPhotos = true,
	} = options;

	const HOUR_HEIGHT = 136; // Scaled for 1.7x resolution
	const TIME_COLUMN_WIDTH = 68; // Width for time labels
	const SCHEDULE_START_Y = HEADER_HEIGHT + 40; // Start after header with some padding
	const usableWidth = canvas.width - PADDING * 2 - TIME_COLUMN_WIDTH;
	const stageWidth = Math.floor(usableWidth / selectedStages.length);

	// Find time range considering overnight schedules
	// For overnight festivals, early morning hours (0-6) are considered part of the previous day
	// So we need to adjust them to be after midnight (24+)
	const adjustedTimes = calendarData.map((set) => {
		let adjustedStart = set.startHour;
		let adjustedEnd = set.startHour + set.duration;

		// If the set starts in early morning (0-6), it's part of the overnight schedule
		if (set.startHour >= 0 && set.startHour < 6) {
			adjustedStart = set.startHour + 24;
			adjustedEnd = adjustedStart + set.duration;
		}
		// If the set crosses midnight (e.g., starts at 23:00, duration 4 hours = ends at 3:00)
		else if (set.startHour >= 18 && adjustedEnd > 24) {
			// Keep start as-is, but ensure end reflects the overnight nature
			// adjustedEnd is already correct (e.g., 27 for 3AM)
		}

		return {
			start: adjustedStart,
			end: adjustedEnd,
		};
	});

	// Get the actual start and end hours
	const minHour = Math.floor(Math.min(...adjustedTimes.map((t) => t.start)));
	const maxHour = Math.ceil(Math.max(...adjustedTimes.map((t) => t.end)));

	// Calculate the hour range for the grid
	const hourRange = maxHour - minHour;

	const gridHeight = hourRange * HOUR_HEIGHT;
	const maxGridHeight = canvas.height - SCHEDULE_START_Y - FOOTER_HEIGHT;
	const actualGridHeight = Math.min(gridHeight, maxGridHeight);

	// Fill entire background with solid color
	ctx.fillStyle = colors.background;
	ctx.fillRect(0, 0, canvas.width, canvas.height);

	// Render smooth gradient background
	renderGradientBackground(ctx, canvas.width, canvas.height, options.gradientColors);

	// Draw user profile at the very top if username provided and schedule has personalized data
	if (options.username && hasPersonalizedData(calendarData, options.reviews)) {
		const userProfileY = HEADER_HEIGHT - 420; // Position above title (same 120px gap as title-to-legend)
		await drawUserProfile(
			ctx,
			canvas.width,
			userProfileY,
			options.username,
			options.userProfileImageUrl,
			colors,
			fontFamily,
		);
	}

	// Draw festival title at the top
	ctx.font = `bold 64px ${fontFamily}`;
	ctx.fillStyle = colors.text;
	ctx.textAlign = "center";
	ctx.textBaseline = "middle";

	// Generate title based on event type and selected day
	let title = festivalName;

	// Add event type label for pre/after parties
	if (eventType === "after_party") {
		title += " Official Afterparties";
	} else if (eventType === "pre_party") {
		title += " Official Pre-Parties";
	}

	// Add the selected day only (not all available days)
	const capitalizedDay =
		selectedDay.charAt(0).toUpperCase() + selectedDay.slice(1);
	title += ` ${capitalizedDay}`;

	ctx.fillText(
		title,
		canvas.width / 2,
		HEADER_HEIGHT - 300,
	);

	// Draw legend in the header area with more vertical spacing
	const legendY = HEADER_HEIGHT - 180;
	const legendItems = [
		{ label: "Must See", color: colors.mustSee },
		{ label: "Want to See", color: colors.wantToSee },
		{ label: "Saw Live", color: colors.sawLive },
		{ label: "Saw Stream", color: colors.sawStream },
	];

	// Calculate total width needed for legend
	ctx.font = `500 42px ${fontFamily}`;
	const itemSpacing = 65;
	const boxSize = 42;
	const boxTextGap = 18;

	let totalWidth = 0;
	const itemWidths: number[] = [];

	for (const item of legendItems) {
		const textWidth = ctx.measureText(item.label).width;
		const itemWidth = boxSize + boxTextGap + textWidth;
		itemWidths.push(itemWidth);
		totalWidth += itemWidth;
	}
	totalWidth += (legendItems.length - 1) * itemSpacing;

	// Draw legend items centered
	let currentX = (canvas.width - totalWidth) / 2;

	legendItems.forEach((item, index) => {
		// Draw colored rectangle - vertically centered with text
		ctx.fillStyle = item.color;
		roundRect(ctx, currentX, legendY - boxSize / 2, boxSize, boxSize, 12);
		ctx.fill();

		// Draw label - ensure vertical alignment
		ctx.fillStyle = colors.textMuted;
		ctx.textAlign = "left";
		ctx.textBaseline = "middle";
		ctx.fillText(item.label, currentX + boxSize + boxTextGap, legendY);

		const width = itemWidths[index] || 0;
		currentX += width + itemSpacing;
	});

	// Reset text baseline for subsequent drawing
	ctx.textBaseline = "alphabetic";

	// Draw stage headers
	ctx.fillStyle = colors.text;
	const stageFontSize = 32;
	ctx.font = `bold ${stageFontSize}px ${fontFamily}`;
	ctx.textAlign = "left";
	ctx.textBaseline = "bottom";
	selectedStages.forEach((stage, index) => {
		const padding = 13;
		const x = PADDING + TIME_COLUMN_WIDTH + index * stageWidth + padding;
		const maxWidth = stageWidth - padding * 2;

		// Better truncation with ellipsis
		let stageName = stage;
		if (ctx.measureText(stageName).width > maxWidth) {
			while (
				ctx.measureText(`${stageName.trimEnd()}...`).width > maxWidth &&
				stageName.length > 0
			) {
				stageName = stageName.slice(0, -1);
			}
			stageName = `${stageName.trimEnd()}...`;
		}
		ctx.fillText(stageName, x, SCHEDULE_START_Y - 10);
	});

	// Draw time labels handling overnight schedules
	ctx.textAlign = "center";
	ctx.font = `bold 42px ${fontFamily}`;
	ctx.fillStyle = colors.text;
	ctx.textBaseline = "middle";

	const isInverse = sortOrder === "inverse";

	// Generate time labels respecting user preferences
	const timeLabels = [];
	const use24Hour = options.userPreferences?.time_format === "24h";

	for (let i = 0; i <= hourRange; i++) {
		const hour = minHour + i;
		let displayText: string;

		if (use24Hour) {
			// 24-hour format: just show the hour as-is (handling overnight properly)
			const displayHour = hour % 24;
			displayText = displayHour.toString().padStart(2, "0");
		} else {
			// 12-hour format: convert to AM/PM
			const displayHour = hour % 24;
			if (displayHour === 0) {
				displayText = "12"; // Midnight shows as 12
			} else if (displayHour > 12) {
				displayText = (displayHour - 12).toString();
			} else if (displayHour === 12) {
				displayText = "12"; // Noon stays as 12
			} else {
				displayText = displayHour.toString();
			}
		}

		timeLabels.push({ hour, displayText });
	}

	// Reverse labels if inverse
	if (isInverse) {
		timeLabels.reverse();
	}

	// Draw time labels
	timeLabels.forEach((label, i) => {
		const y = SCHEDULE_START_Y + i * HOUR_HEIGHT;

		// Only draw if within visible area
		if (y > canvas.height - FOOTER_HEIGHT) return;

		// Display the formatted time
		ctx.fillText(label.displayText, PADDING + TIME_COLUMN_WIDTH / 2, y);

		// Draw horizontal grid lines
		ctx.strokeStyle = colors.border;
		ctx.lineWidth = 1;
		ctx.beginPath();
		ctx.moveTo(PADDING, y);
		ctx.lineTo(canvas.width - PADDING, y);
		ctx.stroke();
	});

	// Draw vertical stage dividers
	ctx.strokeStyle = colors.border;
	ctx.lineWidth = 1;
	for (let i = 0; i <= selectedStages.length; i++) {
		const x = PADDING + TIME_COLUMN_WIDTH + i * stageWidth;
		ctx.beginPath();
		ctx.moveTo(x, SCHEDULE_START_Y);
		ctx.lineTo(x, SCHEDULE_START_Y + actualGridHeight);
		ctx.stroke();
	}

	// Preload artist images (only if photos enabled)
	const artistImages = new Map<string, HTMLImageElement>();
	if (showArtistPhotos) {
		const imageLoadPromises = calendarData
			.filter((set) => set.imageUrl)
			.map(async (set) => {
				try {
					const img = await loadImage(set.imageUrl!, 3000);
					artistImages.set(set.id, img);
				} catch {
					// Silently skip failed image loads
				}
			});
		await Promise.all(imageLoadPromises);
	}

	// Draw sets
	calendarData.forEach((set) => {
		const stageIndex = selectedStages.indexOf(set.stage);
		if (stageIndex === -1) return;

		const x = PADDING + TIME_COLUMN_WIDTH + stageIndex * stageWidth + 2;

		// Adjust start hour for overnight sets
		let adjustedStartHour = set.startHour;
		if (set.startHour >= 0 && set.startHour < 6) {
			adjustedStartHour = set.startHour + 24;
		}

		// Position based on adjusted hour offset
		let y: number;
		const height = set.duration * HOUR_HEIGHT - 4;
		const width = stageWidth - 4;

		if (isInverse) {
			// For inverse order, position sets from bottom to top
			const adjustedEndHour = adjustedStartHour + set.duration;
			const endHourOffset = adjustedEndHour - minHour;
			const hoursFromEnd = hourRange - endHourOffset;
			y = SCHEDULE_START_Y + hoursFromEnd * HOUR_HEIGHT + 2;
		} else {
			// Standard order
			const hourOffset = adjustedStartHour - minHour;
			y = SCHEDULE_START_Y + hourOffset * HOUR_HEIGHT + 2;
		}

		// Skip sets that are outside visible area
		if (y > canvas.height - FOOTER_HEIGHT) return;
		if (y + height < SCHEDULE_START_Y) return;

		// Find matching review
		const review = options.reviews.find((r) => r.setId === set.id);

		// Draw set card - solid colors matching our UI
		let backgroundColor = colors.border; // Default gray for no status
		let textColor = colors.text;

		// Priority order: must see > want to see > saw
		if (set.status.mustSee || review?.mustSee) {
			backgroundColor = colors.mustSee;
			textColor = "#ffffff";
		} else if (set.status.isInSchedule || review?.wantToSee) {
			backgroundColor = colors.wantToSee;
			textColor = "#ffffff";
		} else if (set.status.sawLive || review?.sawLive) {
			backgroundColor = colors.sawLive;
			textColor = "#ffffff";
		} else if (set.status.sawStream || review?.sawStream) {
			backgroundColor = colors.sawStream;
			textColor = "#ffffff";
		}

		ctx.fillStyle = backgroundColor;
		roundRect(ctx, x, y, width, height, 16);
		ctx.fill();

		// Use the artist name from calendarData which already has proper B2B formatting
		const artistDisplay = set.artist;

		// Draw artist name with larger font for better readability
		ctx.fillStyle = textColor;
		const artistFontSize =
			selectedStages.length >= 7
				? 32
				: selectedStages.length >= 5
					? 36
					: selectedStages.length > 3
						? 40
						: 44;
		ctx.font = `600 ${artistFontSize}px ${fontFamily}`;
		ctx.textAlign = "left";
		ctx.textBaseline = "top";

		// Multi-line text wrapping with proper padding
		const padding = 14; // Match in-app calendar feel at 1.7x scale
		const lineHeight = artistFontSize + 4; // Comfortable line spacing

		// Draw artist photo or placeholder if set is tall enough and photos enabled
		const artistImg = artistImages.get(set.id);
		const photoSize = Math.min(artistFontSize * 2, height - padding * 2, width * 0.3);
		const showPhotoSlot = showArtistPhotos && height >= 60 && photoSize >= 24;
		const photoOffset = showPhotoSlot ? photoSize + padding : 0;

		if (showPhotoSlot) {
			const photoX = x + padding;
			const photoY = y + padding;
			const photoRadius = 12;

			if (artistImg) {
				ctx.save();
				roundRect(ctx, photoX, photoY, photoSize, photoSize, photoRadius);
				ctx.clip();
				ctx.drawImage(artistImg, photoX, photoY, photoSize, photoSize);
				ctx.restore();
			} else {
				// Draw placeholder background
				ctx.fillStyle = colors.placeholder;
				roundRect(ctx, photoX, photoY, photoSize, photoSize, photoRadius);
				ctx.fill();
				// Draw mic icon
				drawMicIcon(ctx, photoX, photoY, photoSize, colors.placeholderIcon);
			}
		}

		const maxTextWidth = width - padding * 2 - photoOffset;

		// Calculate time display requirements
		const timeFontSize =
			selectedStages.length >= 7
				? 24
				: selectedStages.length >= 5
					? 26
					: selectedStages.length > 3
						? 28
						: 30;
		const timeLineHeight = timeFontSize + 4; // Reduced from +8 to +4 for tighter time spacing
		const hasTimeData = set.time && set.time !== "TBA";

		// Calculate available space more conservatively - ensure we always have room for at least 1 line of artist text
		const totalAvailableHeight = height - padding * 2;
		const minArtistSpace = lineHeight; // Always reserve space for at least 1 line
		const maxTimeLines = 2; // Allow time to wrap to 2 lines (matches web calendar line-clamp-2)
		const timeGap = 2; // Gap between artist text and time
		const singleTimeLineSpace = timeLineHeight + timeGap;

		// Only reserve time space if we have enough room for artist + at least 1 line of time
		const canShowTime =
			totalAvailableHeight >= minArtistSpace + singleTimeLineSpace;
		// Reserve 1 line for time in layout calculation; 2nd line is opportunistic (uses leftover space)
		const reservedTimeSpace = canShowTime && hasTimeData ? singleTimeLineSpace : 0;
		const availableHeightForArtist = totalAvailableHeight - reservedTimeSpace;
		const maxLines = Math.floor(availableHeightForArtist / lineHeight);

		// Only render text for sets that are tall enough (>= 25px height)
		if (height >= 25) {
			// Function to wrap text into lines
			const wrapText = (text: string, maxWidth: number): string[] => {
				const words = text.split(" ");
				const lines: string[] = [];
				let currentLine = "";

				for (const word of words) {
					// Check if the word itself is too long
					if (ctx.measureText(word).width > maxWidth) {
						// If we have a current line, push it first
						if (currentLine) {
							lines.push(currentLine);
							currentLine = "";
						}

						// Break the long word
						let remainingWord = word;
						while (
							remainingWord.length > 0 &&
							ctx.measureText(remainingWord).width > maxWidth
						) {
							// Find how many characters fit
							let charCount = remainingWord.length;
							while (
								charCount > 0 &&
								ctx.measureText(remainingWord.substring(0, charCount)).width >
									maxWidth
							) {
								charCount--;
							}
							if (charCount === 0) charCount = 1; // At least one character

							lines.push(remainingWord.substring(0, charCount));
							remainingWord = remainingWord.substring(charCount);
						}

						if (remainingWord) {
							currentLine = remainingWord;
						}
					} else {
						const testLine = currentLine ? `${currentLine} ${word}` : word;
						const testWidth = ctx.measureText(testLine).width;

						if (testWidth > maxWidth && currentLine) {
							lines.push(currentLine);
							currentLine = word;
						} else {
							currentLine = testLine;
						}
					}
				}

				if (currentLine) {
					lines.push(currentLine);
				}

				return lines;
			};

			// Wrap text and calculate max lines based on set height
			const wrappedLines = wrapText(artistDisplay, maxTextWidth);

			// Smart line calculation based on set duration/height
			// For sets 2+ hours, allow more lines. For shorter sets, be more conservative.
			const setDurationHours = set.duration;
			let maxDisplayLines: number;

			if (setDurationHours >= 2) {
				// Long sets (2+ hours) can show up to 5 lines
				maxDisplayLines = 5;
			} else if (setDurationHours >= 1) {
				// Medium sets (1-2 hours) can show up to 4 lines
				maxDisplayLines = 4;
			} else if (setDurationHours >= 0.5) {
				// Medium-short sets (30+ minutes) can show up to 3 lines
				maxDisplayLines = 3;
			} else {
				// Very short sets (< 30 minutes) limited to 2 lines
				maxDisplayLines = 2;
			}

			const linesToDraw = Math.min(
				wrappedLines.length,
				maxDisplayLines,
				maxLines,
			);

			// Helper to draw text with muted connectors
			const drawLineWithMutedConnectors = (
				line: string,
				lineX: number,
				lineY: number,
				primaryColor: string,
				mutedColor: string,
			) => {
				const segments = parseArtistSegments(line);
				let currentX = lineX;
				for (const segment of segments) {
					ctx.fillStyle = segment.isConnector ? mutedColor : primaryColor;
					ctx.fillText(segment.text, currentX, lineY);
					currentX += ctx.measureText(segment.text).width;
				}
			};

			// Determine muted color for connectors (more visible difference)
			const mutedConnectorColor = textColor === "#ffffff"
				? "rgba(255, 255, 255, 0.5)"
				: colors.textMuted;

			for (let i = 0; i < linesToDraw; i++) {
				let line = wrappedLines[i];
				if (!line) continue;

				// Only add ellipsis if we've hit the maximum possible lines for this set size
				if (
					i === linesToDraw - 1 &&
					wrappedLines.length > linesToDraw &&
					linesToDraw === maxDisplayLines
				) {
					// Trim the last line to fit with ellipsis
					while (
						ctx.measureText(`${line}...`).width > maxTextWidth &&
						line.length > 0
					) {
						line = line.slice(0, -1).trim();
					}
					line = `${line}...`;
				}

				drawLineWithMutedConnectors(
					line,
					x + padding + photoOffset,
					y + padding + i * lineHeight,
					textColor,
					mutedConnectorColor,
				);
			}

			// Draw time display below artist name if we have time data and space
			if (hasTimeData && canShowTime) {
				try {
					// Parse the time from set.time which is in format like "4:00PM-5:00PM"
					const timeMatch = set.time.match(/^(.+?)-(.+?)$/);
					if (timeMatch?.[1] && timeMatch?.[2]) {
						const [, startTimeStr, endTimeStr] = timeMatch;

						// Create date objects for today with proper time parsing
						const today = new Date();
						const todayDateString = today.toISOString().split("T")[0]; // Get YYYY-MM-DD format

						// Convert to 24-hour format and create ISO strings
						const startTime24 = convertTo24Hour(startTimeStr.trim());
						const endTime24 = convertTo24Hour(endTimeStr.trim());

						const startTime = new Date(`${todayDateString}T${startTime24}:00`);
						const endTime = new Date(`${todayDateString}T${endTime24}:00`);

						// Validate the dates are valid
						if (
							!Number.isNaN(startTime.getTime()) &&
							!Number.isNaN(endTime.getTime())
						) {
							// Format using user preferences (without day, just time range)
							// Strip :00 to save space in export (e.g. "7:00 PM" → "7 PM", "19:00" → "19")
							const formattedTimeRange = formatTimeRangeWithPreferences(
								startTime,
								endTime,
								options.userPreferences || { time_format: "12h" },
							).replace(/:00/g, "");

							// Set up time text styling with adaptive color
							ctx.font = `500 ${timeFontSize}px ${fontFamily}`;

							// Always use muted color for time text
							ctx.fillStyle = textColor === "#ffffff"
								? "rgba(255, 255, 255, 0.6)"
								: colors.textMuted;

							// Calculate time display position (below the artist text)
							const timeY = y + padding + linesToDraw * lineHeight + timeGap;
							const timeMaxWidth = maxTextWidth;
							const bottomBound = y + height - padding;

							// Wrap time text to multiple lines if needed (up to maxTimeLines)
							const timeTextWidth = ctx.measureText(formattedTimeRange).width;
							if (timeTextWidth <= timeMaxWidth) {
								// Fits on one line
								if (timeY + timeLineHeight <= bottomBound) {
									ctx.fillText(formattedTimeRange, x + padding + photoOffset, timeY);
								}
							} else {
								// Wrap: split at " - " or " – " separator for clean break
								const separator = formattedTimeRange.includes(" – ") ? " – " : " - ";
								const timeParts = formattedTimeRange.split(separator);
								let timeLines: string[];
								if (timeParts.length === 2 && timeParts[0] && timeParts[1]) {
									timeLines = [timeParts[0] + separator.trimEnd(), timeParts[1]];
								} else {
									// Fallback: hard wrap
									timeLines = [formattedTimeRange];
								}
								for (let tl = 0; tl < Math.min(timeLines.length, maxTimeLines); tl++) {
									const tlY = timeY + tl * timeLineHeight;
									const timeLine = timeLines[tl];
									if (timeLine && tlY + timeLineHeight <= bottomBound) {
										ctx.fillText(timeLine, x + padding + photoOffset, tlY);
									}
								}
							}
						}
					}
				} catch (error) {
					// Silently fail for invalid time formats - just don't show time
					console.warn(
						"Failed to parse time for set:",
						set.artist,
						set.time,
						error,
					);
				}
			}
		} else {
			// Text is skipped for very short sets
		}
	});

	// Branding at bottom - logo + URL (same pattern as review-share-webview.tsx)
	const brandingY = canvas.height - 400;
	const humbleLogoSize = 48;
	const brandingGap = 16;
	const urlText = "tryhumble.com";
	const brandingUrlFontSize = 40;
	ctx.font = `500 ${brandingUrlFontSize}px ${fontFamily}`;
	const urlWidth = ctx.measureText(urlText).width;
	const totalBrandingWidth = humbleLogoSize + brandingGap + urlWidth;
	const brandingStartX = (canvas.width - totalBrandingWidth) / 2;

	// Try to load Humble logo
	try {
		const logoImg = await loadImage("https://app.tryhumble.com/icon-120x120.png", 3000);
		const logoX = brandingStartX;
		const logoRadius = 8;
		ctx.save();
		roundRect(ctx, logoX, brandingY, humbleLogoSize, humbleLogoSize, logoRadius);
		ctx.clip();
		ctx.drawImage(logoImg, logoX, brandingY, humbleLogoSize, humbleLogoSize);
		ctx.restore();
		ctx.font = `500 ${brandingUrlFontSize}px ${fontFamily}`;
		ctx.fillStyle = colors.text;
		ctx.textAlign = "left";
		ctx.textBaseline = "middle";
		ctx.fillText(urlText, logoX + humbleLogoSize + brandingGap, brandingY + humbleLogoSize / 2);
	} catch (e) {
		// Fallback to just "humble" centered
		ctx.font = `600 36px ${fontFamily}`;
		ctx.fillStyle = colors.text;
		ctx.textAlign = "center";
		ctx.textBaseline = "middle";
		ctx.fillText("humble", canvas.width / 2, brandingY + humbleLogoSize / 2);
	}
}

export async function exportScheduleAsImage(
	options: ExportOptions,
): Promise<void> {
	const {
		festivalSlug,
		selectedDay,
		theme,
		sortOrder,
		phoneSize,
		calendarData,
		reviews,
		selectedStages,
		eventType = "main_festival",
	} = options;

	// Create canvas
	const canvas = document.createElement("canvas");
	const ctx = canvas.getContext("2d");
	if (!ctx) throw new Error("Canvas not supported");

	// Load Inter font with fallback to system fonts
	const fontFamily = await loadInterFont();

	// Determine if we're doing grid view (when stages are selected)
	const isGridView = selectedStages && selectedStages.length > 0;

	// Scale up by 1.7x to account for iOS zoom behavior
	// This ensures content is properly sized when iOS zooms in
	const SCALE_FACTOR = 1.7;
	canvas.width = Math.round(phoneSize.width * SCALE_FACTOR);
	canvas.height = Math.round(phoneSize.height * SCALE_FACTOR);

	// No offset needed - we'll position content using the large header/footer heights
	ctx.save();

	// Theme colors - using Radix Gray scale to match web design system
	// Use unified color system (matches server and app design)
	const colors = getExportColors(theme);

	// Validate contrast ratios to ensure text visibility on all devices
	try {
		validateExportColors(colors);
	} catch (error) {
		// Log warning but don't block export
		console.warn("⚠️ Color contrast validation warning:", error);
	}

	// Fill entire canvas background
	ctx.fillStyle = colors.background;
	ctx.fillRect(0, 0, canvas.width, canvas.height);

	// Render mesh gradient background using WebGL shader with selected colors
	renderGradientBackground(ctx, canvas.width, canvas.height, options.gradientColors);

	// Draw user profile at the very top if username provided and schedule has personalized data
	if (!isGridView && options.username && hasPersonalizedData(calendarData, reviews)) {
		const userProfileY = HEADER_HEIGHT - 420; // Position above title (same 120px gap as title-to-legend)
		await drawUserProfile(
			ctx,
			canvas.width,
			userProfileY,
			options.username,
			options.userProfileImageUrl,
			colors,
			fontFamily,
		);
	}

	// Draw festival title at the top
	ctx.font = `bold 64px ${fontFamily}`;
	ctx.fillStyle = colors.text;
	ctx.textAlign = "center";
	ctx.textBaseline = "middle";

	// Generate title based on event type and selected day
	let title = options.festivalName;

	// Add event type label for pre/after parties
	if (eventType === "after_party") {
		title += " Official Afterparties";
	} else if (eventType === "pre_party") {
		title += " Official Pre-Parties";
	}

	// Add the selected day only (not all available days)
	const capitalizedDay =
		selectedDay.charAt(0).toUpperCase() + selectedDay.slice(1);
	title += ` ${capitalizedDay}`;

	ctx.fillText(
		title,
		canvas.width / 2,
		HEADER_HEIGHT - 300,
	);

	// Draw legend in the header area with more vertical spacing
	const legendY = HEADER_HEIGHT - 180;
	const legendItems = [
		{ label: "Must See", color: colors.mustSee },
		{ label: "Want to See", color: colors.wantToSee },
		{ label: "Saw Live", color: colors.sawLive },
		{ label: "Saw Stream", color: colors.sawStream },
	];

	// Calculate total width needed for legend
	ctx.font = `500 42px ${fontFamily}`;
	const itemSpacing = 65;
	const boxSize = 42;
	const boxTextGap = 18;

	let totalWidth = 0;
	const itemWidths: number[] = [];

	for (const item of legendItems) {
		const textWidth = ctx.measureText(item.label).width;
		const itemWidth = boxSize + boxTextGap + textWidth;
		itemWidths.push(itemWidth);
		totalWidth += itemWidth;
	}
	totalWidth += (legendItems.length - 1) * itemSpacing;

	// Draw legend items centered
	let currentX = (canvas.width - totalWidth) / 2;

	legendItems.forEach((item, index) => {
		// Draw colored rectangle - vertically centered with text
		ctx.fillStyle = item.color;
		roundRect(ctx, currentX, legendY - boxSize / 2, boxSize, boxSize, 12);
		ctx.fill();

		// Draw label - ensure vertical alignment
		ctx.fillStyle = colors.textMuted;
		ctx.textAlign = "left";
		ctx.textBaseline = "middle";
		ctx.fillText(item.label, currentX + boxSize + boxTextGap, legendY);

		const width = itemWidths[index] || 0;
		currentX += width + itemSpacing;
	});

	// Reset text baseline for subsequent drawing
	ctx.textBaseline = "alphabetic";

	// URL will be drawn at the bottom of the calendar content instead

	// Draw schedule content based on view type
	if (isGridView) {
		// Use grid view for calendar export
		await exportGridSchedule(ctx, canvas, { ...options, colors }, roundRect, fontFamily);
	} else {
		// Original list view
		const sortedData = [...calendarData];
		if (sortOrder === "inverse") {
			sortedData.sort((a, b) => {
				const aEndHour = a.startHour + a.duration;
				const bEndHour = b.startHour + b.duration;
				return bEndHour - aEndHour;
			});
		} else {
			sortedData.sort((a, b) => a.startHour - b.startHour);
		}

		// Preload artist images for list view (only if photos enabled)
		const showPhotos = options.showArtistPhotos ?? true;
		const listArtistImages = new Map<string, HTMLImageElement>();
		if (showPhotos) {
			const listImagePromises = sortedData
				.filter((set) => set.imageUrl)
				.map(async (set) => {
					try {
						const img = await loadImage(set.imageUrl!, 3000);
						listArtistImages.set(set.id, img);
					} catch {
						// Silently skip failed image loads
					}
				});
			await Promise.all(listImagePromises);
		}

		let yOffset = HEADER_HEIGHT;

		for (const set of sortedData) {
			// Find matching review
			const review = reviews.find((r) => r.setId === set.id);

			// Determine background color based on status
			let cardColor = colors.card;
			let borderColor = colors.border;

			if (set.status.mustSee || review?.mustSee) {
				cardColor = `${colors.mustSee}15`;
				borderColor = colors.mustSee;
			} else if (set.status.isInSchedule || review?.wantToSee) {
				cardColor = `${colors.wantToSee}15`;
				borderColor = colors.wantToSee;
			} else if (
				set.status.sawLive ||
				set.status.sawStream ||
				review?.sawLive ||
				review?.sawStream
			) {
				cardColor = `${colors.sawLive}15`;
				borderColor = colors.sawLive;
			}

			// Draw card background
			ctx.fillStyle = cardColor;
			ctx.strokeStyle = borderColor;
			ctx.lineWidth = 2;
			roundRect(
				ctx,
				PADDING,
				yOffset,
				canvas.width - PADDING * 2,
				SET_ITEM_HEIGHT,
				20,
			);
			ctx.fill();
			ctx.stroke();

			// Draw artist photo or placeholder
			const listPhotoSize = 56;
			const listImg = listArtistImages.get(set.id);
			const listPhotoOffset = showPhotos ? listPhotoSize + 12 : 0;

			if (showPhotos) {
				const photoX = PADDING + 12;
				const photoY = yOffset + (SET_ITEM_HEIGHT - listPhotoSize) / 2;

				if (listImg) {
					ctx.save();
					roundRect(ctx, photoX, photoY, listPhotoSize, listPhotoSize, 12);
					ctx.clip();
					ctx.drawImage(listImg, photoX, photoY, listPhotoSize, listPhotoSize);
					ctx.restore();
				} else {
					// Draw placeholder background
					ctx.fillStyle = colors.placeholder;
					roundRect(ctx, photoX, photoY, listPhotoSize, listPhotoSize, 12);
					ctx.fill();
					// Draw mic icon
					drawMicIcon(ctx, photoX, photoY, listPhotoSize, colors.placeholderIcon);
				}
			}

			// Draw artist name
			ctx.fillStyle = colors.text;
			ctx.font = `600 30px ${fontFamily}`;
			ctx.textAlign = "left";

			// Use the artist name from calendarData which already has proper B2B formatting
			const artistDisplay = set.artist;

			// Truncate long artist names
			const textStartX = PADDING + 20 + listPhotoOffset;
			const maxTextWidth = canvas.width - PADDING * 2 - 40 - listPhotoOffset;
			let displayText = artistDisplay;

			// Measure text and truncate if needed
			while (
				ctx.measureText(`${displayText.trimEnd()}...`).width > maxTextWidth &&
				displayText.length > 0
			) {
				displayText = displayText.slice(0, -1);
			}
			if (displayText !== artistDisplay) {
				displayText = `${displayText.trimEnd()}...`;
			}

			ctx.fillText(displayText, textStartX, yOffset + 33);

			// Draw time and stage
			ctx.font = `22px ${fontFamily}`;
			ctx.fillStyle = colors.textMuted;
			ctx.fillText(`${set.time} • ${set.stage}`, textStartX, yOffset + 60);

			yOffset += SET_ITEM_HEIGHT + SET_ITEM_SPACING;
		}

		// Branding at bottom - logo + URL (same pattern as review-share-webview.tsx)
		const brandingY = canvas.height - 400;
		const humbleLogoSize = 48;
		const brandingGap = 16;
		const urlText = "tryhumble.com";
		const brandingUrlFontSize = 40;
		ctx.font = `500 ${brandingUrlFontSize}px ${fontFamily}`;
		const urlWidth = ctx.measureText(urlText).width;
		const totalBrandingWidth = humbleLogoSize + brandingGap + urlWidth;
		const brandingStartX = (canvas.width - totalBrandingWidth) / 2;

		// Try to load Humble logo
		try {
			const logoImg = await loadImage("https://app.tryhumble.com/icon-120x120.png", 3000);
			const logoX = brandingStartX;
			const logoRadius = 8;
			ctx.save();
			roundRect(ctx, logoX, brandingY, humbleLogoSize, humbleLogoSize, logoRadius);
			ctx.clip();
			ctx.drawImage(logoImg, logoX, brandingY, humbleLogoSize, humbleLogoSize);
			ctx.restore();
			ctx.font = `500 ${brandingUrlFontSize}px ${fontFamily}`;
			ctx.fillStyle = colors.text;
			ctx.textAlign = "left";
			ctx.textBaseline = "middle";
			ctx.fillText(urlText, logoX + humbleLogoSize + brandingGap, brandingY + humbleLogoSize / 2);
		} catch (e) {
			// Fallback to just "humble" centered
			ctx.font = `600 36px ${fontFamily}`;
			ctx.fillStyle = colors.text;
			ctx.textAlign = "center";
			ctx.textBaseline = "middle";
			ctx.fillText("humble", canvas.width / 2, brandingY + humbleLogoSize / 2);
		}
	}

	// Leave bottom area empty for lock screen controls

	// Restore the context to remove the translation
	ctx.restore();

	// Convert to blob and download
	return new Promise<void>((resolve, reject) => {
		canvas.toBlob((blob) => {
			if (!blob) {
				reject(new Error("Failed to create image"));
				return;
			}

			const url = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = `${festivalSlug}-${selectedDay.toLowerCase().replace(/\s+/g, "-")}-schedule.png`;
			a.style.display = "none";
			document.body.appendChild(a);
			a.click();

			// Clean up immediately after click
			setTimeout(() => {
				document.body.removeChild(a);
				URL.revokeObjectURL(url);
				resolve();
			}, 100);
		}, "image/png");
	});
}

// Helper function to draw rounded rectangles
function roundRect(
	ctx: CanvasRenderingContext2D,
	x: number,
	y: number,
	width: number,
	height: number,
	radius: number,
) {
	ctx.beginPath();
	ctx.moveTo(x + radius, y);
	ctx.lineTo(x + width - radius, y);
	ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
	ctx.lineTo(x + width, y + height - radius);
	ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
	ctx.lineTo(x + radius, y + height);
	ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
	ctx.lineTo(x, y + radius);
	ctx.quadraticCurveTo(x, y, x + radius, y);
	ctx.closePath();
}

// Helper function to draw a mic icon centered in a box (matches RiMicLine)
function drawMicIcon(
	ctx: CanvasRenderingContext2D,
	boxX: number,
	boxY: number,
	boxSize: number,
	color: string,
) {
	const iconSize = boxSize * 0.4;
	const cx = boxX + boxSize / 2;
	const cy = boxY + boxSize / 2;
	const micWidth = iconSize * 0.4;
	const micHeight = iconSize * 0.55;
	const micRadius = micWidth;

	ctx.strokeStyle = color;
	ctx.fillStyle = "transparent";
	ctx.lineWidth = Math.max(1.5, iconSize * 0.1);
	ctx.lineCap = "round";

	// Mic body (rounded rect)
	ctx.beginPath();
	const micX = cx - micWidth / 2;
	const micY = cy - micHeight / 2 - iconSize * 0.08;
	ctx.moveTo(micX + micRadius, micY);
	ctx.lineTo(micX + micWidth - micRadius, micY);
	ctx.quadraticCurveTo(micX + micWidth, micY, micX + micWidth, micY + micRadius);
	ctx.lineTo(micX + micWidth, micY + micHeight - micRadius);
	ctx.quadraticCurveTo(micX + micWidth, micY + micHeight, micX + micWidth - micRadius, micY + micHeight);
	ctx.lineTo(micX + micRadius, micY + micHeight);
	ctx.quadraticCurveTo(micX, micY + micHeight, micX, micY + micHeight - micRadius);
	ctx.lineTo(micX, micY + micRadius);
	ctx.quadraticCurveTo(micX, micY, micX + micRadius, micY);
	ctx.closePath();
	ctx.stroke();

	// Arc below mic (the "cup" holder)
	const arcRadius = micWidth * 0.9;
	const arcY = micY + micHeight * 0.6;
	ctx.beginPath();
	ctx.arc(cx, arcY, arcRadius, 0, Math.PI);
	ctx.stroke();

	// Stem line down from arc
	const stemTop = arcY + arcRadius;
	const stemBottom = stemTop + iconSize * 0.18;
	ctx.beginPath();
	ctx.moveTo(cx, stemTop);
	ctx.lineTo(cx, stemBottom);
	ctx.stroke();

	// Base line
	const baseHalf = iconSize * 0.18;
	ctx.beginPath();
	ctx.moveTo(cx - baseHalf, stemBottom);
	ctx.lineTo(cx + baseHalf, stemBottom);
	ctx.stroke();
}

// Helper function to draw circles (for user profile)
function drawCircle(
	ctx: CanvasRenderingContext2D,
	x: number,
	y: number,
	radius: number,
) {
	ctx.beginPath();
	ctx.arc(x, y, radius, 0, Math.PI * 2);
	ctx.closePath();
}

// Helper function to check if schedule has personalized data
function hasPersonalizedData(
	calendarData: ExportOptions["calendarData"],
	reviews: Review[],
): boolean {
	return (
		calendarData.some(
			(set) =>
				set.status.mustSee ||
				set.status.isInSchedule ||
				set.status.sawLive ||
				set.status.sawStream,
		) || reviews.length > 0
	);
}

// Helper function to draw user profile section
async function drawUserProfile(
	ctx: CanvasRenderingContext2D,
	canvasWidth: number,
	y: number,
	username: string,
	userProfileImageUrl: string | undefined,
	colors: ColorScheme,
	fontFamily: string,
): Promise<number> {
	const userProfileSize = 56;
	const userProfileGap = 12;
	const usernameFontSize = 40;
	const usernameText = `@${username}`;

	ctx.font = `500 ${usernameFontSize}px ${fontFamily}`;
	const usernameWidth = ctx.measureText(usernameText).width;
	const totalUserWidth = userProfileSize + userProfileGap + usernameWidth;
	const userStartX = (canvasWidth - totalUserWidth) / 2;

	// Draw user profile picture (circular)
	if (userProfileImageUrl) {
		try {
			const userImg = await loadImage(userProfileImageUrl, 3000);
			ctx.save();
			drawCircle(
				ctx,
				userStartX + userProfileSize / 2,
				y + userProfileSize / 2,
				userProfileSize / 2,
			);
			ctx.clip();
			const imgAspect = userImg.width / userImg.height;
			let drawWidth: number;
			let drawHeight: number;
			let drawX: number;
			let drawY: number;
			if (imgAspect > 1) {
				drawHeight = userProfileSize;
				drawWidth = drawHeight * imgAspect;
				drawX = userStartX + (userProfileSize - drawWidth) / 2;
				drawY = y;
			} else {
				drawWidth = userProfileSize;
				drawHeight = drawWidth / imgAspect;
				drawX = userStartX;
				drawY = y + (userProfileSize - drawHeight) / 2;
			}
			ctx.drawImage(userImg, drawX, drawY, drawWidth, drawHeight);
			ctx.restore();
		} catch {
			// Fallback circle with initial
			drawProfileFallback(ctx, userStartX, y, userProfileSize, username, colors, fontFamily);
		}
	} else {
		// Fallback circle with initial
		drawProfileFallback(ctx, userStartX, y, userProfileSize, username, colors, fontFamily);
	}

	// Draw username text
	ctx.font = `500 ${usernameFontSize}px ${fontFamily}`;
	ctx.fillStyle = colors.textMuted;
	ctx.textAlign = "left";
	ctx.textBaseline = "middle";
	ctx.fillText(
		usernameText,
		userStartX + userProfileSize + userProfileGap,
		y + userProfileSize / 2,
	);

	return userProfileSize;
}

// Helper to draw fallback profile circle with initial
function drawProfileFallback(
	ctx: CanvasRenderingContext2D,
	x: number,
	y: number,
	size: number,
	username: string,
	colors: ColorScheme,
	fontFamily: string,
) {
	ctx.fillStyle = "rgba(128,128,128,0.3)";
	drawCircle(ctx, x + size / 2, y + size / 2, size / 2);
	ctx.fill();
	ctx.font = `600 ${size * 0.5}px ${fontFamily}`;
	ctx.fillStyle = colors.textMuted;
	ctx.textAlign = "center";
	ctx.textBaseline = "middle";
	ctx.fillText(username.charAt(0).toUpperCase(), x + size / 2, y + size / 2);
}
